import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Box, Button, MenuItem, TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import ConfirmDialog from "../../../components/ConfirmDialog";
import GoverningBodiesMultiSelect from "../../../components/FormFields/GoverningBodiesMultiSelect";
import { rulesApi } from "../../../utils/rulesApi";
import { useRuleStyles } from "../styles";

const MAX_NAME = 255;

const toOption = (gb) => ({
  id: gb.id,
  abbreviation: gb.abbreviation || gb.shortName || "",
  name: gb.shortName || gb.name || "",
});

/**
 * Create or edit a rule group: name, status (edit only) and the governing bodies it is offered
 * to. The server diffs `governingBodyIds`, so saving here never re-orders a governing body's
 * existing rule-group sequence.
 */
const RuleGroupDialog = ({ open, group, onClose, onSaved }) => {
  const classes = useRuleStyles();
  const isNew = !group;
  const [name, setName] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [governingBodies, setGoverningBodies] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(group?.name || "");
    setStatus(group?.status || "ACTIVE");
    setGoverningBodies((group?.governingBodies || []).map(toOption));
    setError(null);
  }, [open, group]);

  const nameError = useMemo(() => {
    const trimmed = name.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length > MAX_NAME) return `At most ${MAX_NAME} characters`;
    return null;
  }, [name]);

  const field = {
    name: "governingBodyIds",
    value: governingBodies,
    onChange: setGoverningBodies,
  };

  const save = async () => {
    if (nameError) return;
    setSaving(true);
    setError(null);
    const body = {
      name: name.trim(),
      status,
      governingBodyIds: governingBodies.map((gb) => gb.id),
    };
    try {
      const saved = isNew
        ? await rulesApi.createGroup(body)
        : await rulesApi.updateGroup(group.id, body);
      toast.success(isNew ? "Rule group created" : "Rule group updated");
      onSaved(saved);
    } catch (e) {
      if (e.status === 409 || e.status === 400) {
        setError(e.message);
      } else {
        toast.error(e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      handleClose={onClose}
      title={isNew ? "New rule group" : `Edit rule group · ${group.name}`}
      disableBackdropClose
    >
      <Box
        component="form"
        className={classes.dialogForm}
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          id="rule-group-name"
          label="Name"
          variant="outlined"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name !== "" && Boolean(nameError)}
          helperText={name !== "" ? nameError : " "}
          inputProps={{ maxLength: MAX_NAME, "aria-required": true }}
          autoFocus
          required
        />
        {!isNew && (
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            helperText={
              status === "DEPRECATED"
                ? "A deprecated group's sets stay usable by their events but cannot receive new sets."
                : " "
            }
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DEPRECATED">Deprecated</MenuItem>
          </TextField>
        )}
        <GoverningBodiesMultiSelect
          field={field}
          label="Governing bodies"
          initialOptions={governingBodies}
        />
        <Box className={classes.dialogActions}>
          <Button className={classes.touch} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            className={classes.touch}
            variant="contained"
            color="primary"
            disabled={saving || Boolean(nameError)}
          >
            {isNew ? "Create" : "Save"}
          </Button>
        </Box>
      </Box>
    </ConfirmDialog>
  );
};

RuleGroupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  group: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.string,
    governingBodies: PropTypes.array,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

RuleGroupDialog.defaultProps = { group: null };

export default RuleGroupDialog;
